import { canCreateJobOfType } from '@setcom/core';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api } from './api';
import type { Meta } from './types';
import { useToast } from '../components/Toast';
import { ApiError } from './api';

export function useMeta() {
  return useQuery({ queryKey: ['meta'], queryFn: () => api.get<Meta>('/meta'), staleTime: 5 * 60_000 });
}

/** mutation ที่แสดงข้อความสำเร็จ/ผิดพลาด และรีเฟรชข้อมูลที่เกี่ยวข้องให้เอง */
export function useAction<TVars, TRes = unknown>(
  fn: (v: TVars) => Promise<TRes>,
  opts: { success?: string | ((r: TRes) => string); invalidate?: QueryKey[]; onSuccess?: (r: TRes, v: TVars) => void; onError?: (e: unknown) => void } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (r, v) => {
      if (opts.success) toast.show(typeof opts.success === 'function' ? opts.success(r) : opts.success, 'success');
      for (const k of opts.invalidate ?? []) await qc.invalidateQueries({ queryKey: k });
      opts.onSuccess?.(r, v);
    },
    onError: (e) => {
      if (opts.onError) opts.onError(e);
      else toast.show(e instanceof ApiError ? e.message : 'เกิดข้อผิดพลาด', 'error');
    },
  });
}

/** ข้อความเมื่อแบบงานจำกัดผู้เปิด (ตรวจจริงที่ server) */
export const RESTRICTED_TYPE_NOTE = 'งาน EV เปิดได้เฉพาะแอดมินที่รับผิดชอบ';

/** ผู้ใช้ปัจจุบันเปิด/ยืนยันงานแบบนี้ได้ไหม — กฎเดียวกับ server (core canCreateJobOfType) */
export function canOpenJobType(meta: Meta | undefined, jobType: { allowedCreatorIds?: string[] } | undefined): boolean {
  if (!meta || !jobType) return true;
  const me = meta.me;
  return canCreateJobOfType(jobType, { kind: me.role === 'service' ? 'service' : 'user', role: me.role, userId: me.id });
}

/** ฟีเจอร์ทดลองเปิดอยู่ไหม (ค่าเริ่มต้นปิด — ดู server lib/features.ts) */
export function useFeature(key: keyof NonNullable<Meta['features']>): boolean {
  const { data } = useMeta();
  return data?.features?.[key] === true;
}

export function can(role: string | undefined, ...roles: string[]): boolean {
  return !!role && (role === 'admin' || roles.includes(role));
}
